import { useRef, useState } from "react"
import { Alert, Button } from "@planningcenter/doxy-web"
import {
  ChurchCenterCustomFieldApiRecordWithRelationships,
  UpdateCustomFieldAttributes,
} from "source/myChurchCenter/Profile/types"
import { Icon } from "source/shared/components"

export function File({
  customField,
  updateAttributes,
}: {
  customField: ChurchCenterCustomFieldApiRecordWithRelationships
  updateAttributes: UpdateCustomFieldAttributes
}) {
  const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB default size limit
  const EXTENSION_BLOCK_LIST = [
    "ade",
    "adp",
    "apk",
    "appx",
    "appxbundle",
    "bat",
    "cab",
    "chm",
    "cmd",
    "com",
    "cpl",
    "dll",
    "dmg",
    "ex",
    "ex_",
    "exe",
    "hta",
    "ins",
    "isp",
    "iso",
    "jar",
    "js",
    "jse",
    "lib",
    "lnk",
    "mde",
    "msc",
    "msi",
    "msix",
    "msixbundle",
    "msp",
    "mst",
    "nsh",
    "pif",
    "ps1",
    "scr",
    "sct",
    "shb",
    "sys",
    "vb",
    "vbe",
    "vbs",
    "vxd",
    "wsc",
    "wsf",
    "wsh",
  ]

  const isUnsupportedExtension = (file: File): boolean => {
    const extension = file.name.split(".").pop()?.toLowerCase() || ""
    return EXTENSION_BLOCK_LIST.includes(extension)
  }
  const exceedsMaxSize = (file: File): boolean => {
    return file.size > MAX_FILE_SIZE
  }

  const fieldName =
    customField.relationships.field_definition.data.attributes.name
  const fieldData = customField.relationships.field_data?.data[0]
  const fieldDataFileName = fieldData?.attributes?.file_name
  const {
    attributes: { editing_rules: editingRules },
  } = customField

  const [fileName, setFileName] = useState(fieldDataFileName || "")
  const [uploadErrors, setUploadErrors] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const valueRef = useRef(fieldDataFileName)

  const disabled =
    editingRules === "view" ||
    !!(editingRules === "add_if_blank" && valueRef.current)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    setUploadErrors([])

    if (!file) return

    if (isUnsupportedExtension(file)) {
      setUploadErrors(["This file type is not supported."])
      return
    }

    if (exceedsMaxSize(file)) {
      setUploadErrors([
        `File size exceeds the maximum allowed (${Math.round(MAX_FILE_SIZE / 1024 / 1024)}MB)`,
      ])
      return
    }

    setFileName(file.name)

    updateAttributes({
      customField,
      record: customField.relationships.field_data?.data[0] || null,
      attributes: { value: file.name },
      file,
    })
  }

  const handleChooseFile = () => {
    fileInputRef.current?.click()
  }

  const handleRemoveFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
    setFileName("")
    setUploadErrors([])

    updateAttributes({
      customField,
      record: customField.relationships.field_data?.data[0] || null,
      attributes: { value: "" },
      hasRemovedFile: true,
    })
  }

  return (
    <div key={customField.id}>
      <div className="d-f">
        <label htmlFor={`file-input-${customField.id}`}>{fieldName}</label>
        {disabled && <Icon symbol="general#lock" className="fs-2 pl-1 pt-1" />}
      </div>

      <input
        type="file"
        id={`file-input-${customField.id}`}
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        disabled={disabled}
      />

      <div className="d-f ai-c mt-1">
        {!fileName ? (
          <Button
            disabled={disabled}
            text="Choose a file"
            size="sm"
            variant="outline"
            onClick={handleChooseFile}
            type="button"
          />
        ) : (
          <div className="d-f ai-c" style={{ width: "100%" }}>
            <Icon
              symbol="general#outlined-generic-file"
              className="mr-1"
              style={{ flexShrink: 0 }}
            />
            <div
              style={{
                flex: "1 1 auto",
                minWidth: 0,
                overflow: "hidden",
                marginRight: "8px",
              }}
            >
              <span
                className="d-b"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fileName}
              </span>
            </div>
            <div style={{ flexShrink: 0, width: "auto" }}>
              <Button
                disabled={disabled}
                text="Remove"
                size="sm"
                variant="outline"
                onClick={handleRemoveFile}
                type="button"
              />
            </div>
          </div>
        )}
      </div>

      {uploadErrors.length > 0 && (
        <div className="mt-2">
          <Alert
            size="sm"
            theme="error"
            text={
              uploadErrors.length === 1
                ? uploadErrors[0]
                : uploadErrors.map((error) => `• ${error}`).join(" ")
            }
          />
        </div>
      )}
    </div>
  )
}
